/**
 * Performance › Segments › one segment (`/performance/segments/{uuid}`)
 * — everything the index card deliberately leaves out.
 *
 * The index answers "which of my audiences is having a bad time". This
 * page answers "why, and how bad compared to everyone else":
 *
 *   1. Reach          — samples in the segment, and its share of the site
 *   2. Vitals         — all five metrics, each against the site baseline
 *   3. Trend          — the same five over time
 *   4. By device      — every device the segment spans, side by side
 *   5. Inside it      — one row per value the segment names, so a
 *                       two-market segment shows which market is the drag
 *
 * Everything here is pixel RUM — real visitors, p75. No synthetic runs,
 * no CrUX. That's stated once by the `PlaneBadge` in the header (the
 * same badge + `RUM_EXPLAINER` tooltip the Performance overview uses),
 * so a reader who learned the badge there reads it the same way here.
 *
 * ── Why there is a device selector ──────────────────────────────────
 *
 * `pixel_site_perf_filtered` always groups by form factor, so what comes
 * back is a p75 per (segment × device), never one number for the segment.
 * That is not a limitation to paper over: averaging per-device p75s is
 * not a percentile of anything, and picking one device silently would
 * make the headline lie for the others. So the device is an explicit
 * control — every number on the page is a real p75 of a real slice, and
 * the mix line under the selector says how much of the segment each
 * device accounts for. Segments that pin a single device never see the
 * control at all, which is most of them.
 *
 * ── Why the trend can be absent ─────────────────────────────────────
 *
 * The daily-series pipe accepts fewer dimensions than the headline pipe
 * until its widening is deployed. Rather than draw a whole-site trend
 * under a segment's name, the card says which dimension it can't honour.
 * See `trendBlockers` in lib/queries/traffic-profiles.
 */
import { ConfirmDialog } from '@/components/primitives/ConfirmDialog';
import { EmptyState } from '@/components/primitives/EmptyState';
import { FilterBar } from '@/components/primitives/FilterBar';
import { MetricTile } from '@/components/primitives/MetricTile';
import { type PageRange, PageRangeSelect } from '@/components/primitives/PageRangePill';
import { Segmented } from '@/components/primitives/Segmented';
import { PlaneBadge, RUM_EXPLAINER } from '@/components/rum/PerformanceSection';
import { ProfileEditorDialog } from '@/components/rum/ProfileEditorDialog';
import { SiteIsland } from '@/components/shell/SiteIsland';
import { Button } from '@/components/ui/button';
import { Menu, MenuItem, MenuPopup, MenuTrigger } from '@/components/ui/menu';
import { Skeleton } from '@/components/ui/skeleton';
import { useActiveSiteUuid } from '@/lib/active-site';
import {
  type MetricBand,
  bandText,
  clsBand,
  fcpBand,
  formatCls,
  formatCount,
  formatMs,
  inpBand,
  lcpBand,
  ttfbBand,
} from '@/lib/format';
import { type PixelPerfRow, useSitePixelPerf, useSitePixelPerfTrend } from '@/lib/queries/pixel';
import {
  PROFILE_DIMENSIONS,
  type ProfileFilters,
  type TrafficProfile,
  dimensionLabel,
  profileFiltersToPerfOpts,
  trendBlockers,
  useDeleteTrafficProfile,
  useTrafficProfiles,
} from '@/lib/queries/traffic-profiles';
import {
  DEVICE_LABELS,
  SEGMENT_DEFAULT_RANGE,
  describeValue,
  describeValues,
} from '@/lib/segments';
import { cn } from '@/lib/utils';
import { Devices as DevicesIcon, DotsThree } from '@phosphor-icons/react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useMemo, useState } from 'react';

export const Route = createFileRoute('/_app/performance/segments/$segmentUuid')({
  component: SegmentDetail,
});

// ─── Metric spec ─────────────────────────────────────────────────────
//
// One entry per metric the perf row carries. Drives the tile row, the
// trend row, the device table and the per-value table, so a metric is
// added in exactly one place.

// TTFB and FCP earn tiles here even though they aren't Core Web Vitals:
// "why is LCP bad" is usually answered by TTFB, and this is the page you
// land on to ask that. The index card stays to the three CWV.
interface MetricSpec {
  key: 'lcp' | 'inp' | 'cls' | 'ttfb' | 'fcp';
  label: string;
  p75: (row: PixelPerfRow) => number | null;
  samples: (row: PixelPerfRow) => number;
  band: (v: number | null | undefined) => MetricBand;
  format: (v: number | null | undefined) => string;
}

const METRICS: readonly MetricSpec[] = [
  {
    key: 'lcp',
    label: 'LCP',
    p75: (r) => r.lcp_p75,
    samples: (r) => r.lcp_sample_count,
    band: lcpBand,
    format: formatMs,
  },
  {
    key: 'inp',
    label: 'INP',
    p75: (r) => r.inp_p75,
    samples: (r) => r.inp_sample_count,
    band: inpBand,
    format: formatMs,
  },
  {
    key: 'cls',
    label: 'CLS',
    p75: (r) => r.cls_p75,
    samples: (r) => r.cls_sample_count,
    band: clsBand,
    format: formatCls,
  },
  {
    key: 'ttfb',
    label: 'TTFB',
    p75: (r) => r.ttfb_p75,
    samples: (r) => r.ttfb_sample_count,
    band: ttfbBand,
    format: formatMs,
  },
  {
    key: 'fcp',
    label: 'FCP',
    p75: (r) => r.fcp_p75,
    samples: (r) => r.fcp_sample_count,
    band: fcpBand,
    format: formatMs,
  },
] as const;

/** Percent change of `value` against `baseline`, or null when either
 *  side is missing / the baseline is zero (an infinite delta is noise,
 *  not information). */
function deltaPct(value: number | null | undefined, baseline: number | null | undefined) {
  if (value == null || baseline == null || baseline === 0) return null;
  return ((value - baseline) / baseline) * 100;
}

/**
 * Whether comparing this segment against the site baseline says
 * anything.
 *
 * Every comparison on this page is same-device — "phones in this
 * segment vs the whole site including desktops" would make every mobile
 * segment look broken. But that means a segment whose ONLY filter is
 * the device IS its own baseline, and the tiles would print a row of
 * "0.0%" that reads like a measurement. Two of the four starter
 * segments ("Mobile visitors", "Desktop visitors") are exactly that, so
 * it's the common case rather than an edge one.
 *
 * The comparison is dropped rather than re-based on all devices: the
 * perf pipe groups by form factor, so there is no cross-device p75 to
 * re-base onto, and averaging the per-device ones would invent a number.
 */
function isComparable(filters: ProfileFilters): boolean {
  return Object.entries(filters).some(
    ([dimension, values]) => dimension !== 'form_factor' && (values?.length ?? 0) > 0,
  );
}

const DEVICE_ORDER = ['phone', 'tablet', 'desktop'];

/** Rows carrying no samples at all are the pipe reporting a device the
 *  segment technically spans but nobody used. Dropping them keeps the
 *  device control honest — it offers what there's data for. */
function withSamples(rows: PixelPerfRow[] | undefined): PixelPerfRow[] {
  return (rows ?? [])
    .filter((r) => r.lcp_sample_count > 0)
    .sort((a, b) => DEVICE_ORDER.indexOf(a.form_factor) - DEVICE_ORDER.indexOf(b.form_factor));
}

// ─── Page ────────────────────────────────────────────────────────────

function SegmentDetail(): ReactElement {
  const { segmentUuid } = Route.useParams();
  const siteUuid = useActiveSiteUuid();
  const navigate = useNavigate();
  const [range, setRange] = useState<PageRange>(SEGMENT_DEFAULT_RANGE);
  const [editorOpen, setEditorOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // The list is already in cache from the index page in the common
  // case, and there's no GET-one endpoint — reusing it keeps this page
  // to zero extra round trips when arrived at by link.
  const segments = useTrafficProfiles(siteUuid);
  const segment = segments.data?.find((p) => p.uuid === segmentUuid);
  const del = useDeleteTrafficProfile();

  return (
    <SiteIsland
      section={segment?.name ?? 'Segment'}
      parent={{ label: 'Segments', href: '/performance/segments' }}
    >
      {segments.isLoading && <Skeleton className="h-64 w-full rounded-xl" />}

      {!segments.isLoading && !segment && (
        <EmptyState
          icon={DevicesIcon}
          title="Segment not found"
          description="It may have been deleted, or it belongs to a different site than the one selected above."
          action={<Button render={<Link to="/performance/segments" />}>Back to segments</Button>}
        />
      )}

      {siteUuid && segment && (
        <div className="space-y-5">
          <SegmentHeader
            segment={segment}
            range={range}
            onRangeChange={setRange}
            onEdit={() => setEditorOpen(true)}
            onDelete={() => setConfirmDelete(true)}
          />

          {segment.resolution === 'ready' ? (
            <SegmentBody segment={segment} siteUuid={siteUuid} range={range} />
          ) : (
            <p className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] px-4 py-3 text-sm text-[var(--color-mute)]">
              Vitals aren't available for this segment yet — reporting doesn't cover{' '}
              {Object.keys(segment.filters)
                .filter((k) => !PROFILE_DIMENSIONS.find((d) => d.key === k)?.ready)
                .map(dimensionLabel)
                .join(', ')}
              . The segment is saved, and this page fills in once it does.
            </p>
          )}

          <ProfileEditorDialog
            siteUuid={siteUuid}
            profile={segment}
            open={editorOpen}
            onOpenChange={setEditorOpen}
          />

          <ConfirmDialog
            open={confirmDelete}
            onOpenChange={setConfirmDelete}
            title={`Delete "${segment.name}"?`}
            description="The segment stops appearing under Performance. Nothing about the underlying traffic data changes."
            confirmLabel="Delete"
            destructive
            loading={del.isPending}
            onConfirm={async () => {
              await del.mutateAsync({ uuid: segment.uuid, siteUuid });
              setConfirmDelete(false);
              await navigate({ to: '/performance/segments' });
            }}
          />
        </div>
      )}
    </SiteIsland>
  );
}

/**
 * The pinned control strip, then what the segment actually is.
 *
 * `FilterBar` is the strip every Performance plane heads with (#537) —
 * the window scopes every number on the page, so it stays answerable
 * once the tiles have scrolled away. There are no audience pickers in
 * it: on this surface the segment IS the audience, and its definition
 * is content rather than a control.
 *
 * That definition — the filter chips — sits directly under the bar
 * rather than behind an "edit" click. It's the first thing to check
 * when a number surprises you.
 */
function SegmentHeader({
  segment,
  range,
  onRangeChange,
  onEdit,
  onDelete,
}: {
  segment: TrafficProfile;
  range: PageRange;
  onRangeChange: (next: PageRange) => void;
  onEdit: () => void;
  onDelete: () => void;
}): ReactElement {
  return (
    <>
      <FilterBar>
        <PlaneBadge label="Real users · RUM" tooltip={RUM_EXPLAINER} />
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <PageRangeSelect value={range} onChange={onRangeChange} />
          <Menu>
            <MenuTrigger
              render={
                <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${segment.name}`} />
              }
            >
              <DotsThree aria-hidden="true" size={16} />
            </MenuTrigger>
            <MenuPopup>
              <MenuItem onClick={onEdit}>Edit</MenuItem>
              <MenuItem onClick={onDelete}>Delete</MenuItem>
            </MenuPopup>
          </Menu>
        </div>
      </FilterBar>

      <header className="space-y-2">
        {segment.description && (
          <p className="text-sm text-[var(--color-mute)]">{segment.description}</p>
        )}
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(segment.filters).map(([dimension, values]) => (
            <span
              key={dimension}
              className="rounded-md bg-[var(--color-paper-alt)] px-2 py-0.5 text-[11px] text-[var(--color-ink-soft)]"
            >
              <span className="text-[var(--color-mute)]">{dimensionLabel(dimension)}: </span>
              {describeValues(dimension, values ?? [])}
            </span>
          ))}
        </div>
      </header>
    </>
  );
}

/** Everything that needs data. Split out so the two headline queries
 *  only mount for segments the pipe can actually answer. */
function SegmentBody({
  segment,
  siteUuid,
  range,
}: {
  segment: TrafficProfile;
  siteUuid: string;
  range: PageRange;
}): ReactElement {
  const filters = segment.filters;
  const perfOpts = useMemo(() => profileFiltersToPerfOpts(filters), [filters]);

  // The segment, per device.
  const perf = useSitePixelPerf(siteUuid, { days: range, ...perfOpts });
  // Everyone, same window, same grouping — the baseline every number on
  // this page is compared against. Unfiltered on purpose: "vs the whole
  // site" is the comparison that makes a segment's number mean something.
  const baseline = useSitePixelPerf(siteUuid, { days: range });

  const rows = withSamples(perf.data);
  const baseRows = withSamples(baseline.data);

  // Device selection. Defaults to whichever device carries the most of
  // the segment's samples — the one whose experience the segment is
  // mostly about.
  const [device, setDevice] = useState<string | null>(null);
  const activeDevice =
    device && rows.some((r) => r.form_factor === device)
      ? device
      : (rows.slice().sort((a, b) => b.lcp_sample_count - a.lcp_sample_count)[0]?.form_factor ??
        null);

  const row = rows.find((r) => r.form_factor === activeDevice) ?? null;
  const baseRow = baseRows.find((r) => r.form_factor === activeDevice) ?? null;

  const segmentSamples = rows.reduce((sum, r) => sum + r.lcp_sample_count, 0);
  const siteSamples = baseRows.reduce((sum, r) => sum + r.lcp_sample_count, 0);

  if (perf.isLoading) return <Skeleton className="h-64 w-full rounded-xl" />;

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={DevicesIcon}
        title="No visitors matched this segment"
        description={`Nobody matching these filters was recorded in the last ${range} days. Try a wider date range, or loosen the segment's filters.`}
      />
    );
  }

  return (
    <div className="space-y-5">
      <ReachLine
        segmentSamples={segmentSamples}
        siteSamples={siteSamples}
        rows={rows}
        range={range}
      />

      {rows.length > 1 && activeDevice && (
        <Segmented
          label="Device"
          value={activeDevice}
          onChange={setDevice}
          options={rows.map((r) => ({
            value: r.form_factor,
            label: `${DEVICE_LABELS[r.form_factor] ?? r.form_factor} (${formatCount(r.lcp_sample_count)})`,
          }))}
        />
      )}

      {row && (
        <VitalsTiles
          row={row}
          baseRow={baseRow}
          siteUuid={siteUuid}
          range={range}
          filters={filters}
          device={row.form_factor}
        />
      )}

      {rows.length > 1 && (
        <DeviceTable rows={rows} baseRows={isComparable(filters) ? baseRows : []} />
      )}

      <ValueSplit
        segment={segment}
        siteUuid={siteUuid}
        range={range}
        device={activeDevice}
        segmentRow={row}
      />
    </div>
  );
}

/** How much of the site this segment is. The share is the number that
 *  turns "LCP is 40% worse here" into a decision — 40% worse for 2% of
 *  traffic is a different problem from 40% worse for half of it. */
function ReachLine({
  segmentSamples,
  siteSamples,
  rows,
  range,
}: {
  segmentSamples: number;
  siteSamples: number;
  rows: PixelPerfRow[];
  range: PageRange;
}): ReactElement {
  const share = siteSamples > 0 ? (segmentSamples / siteSamples) * 100 : null;
  return (
    <p className="text-sm text-[var(--color-mute)]">
      <span className="font-medium text-[var(--color-ink)]">
        {formatCount(segmentSamples)} samples
      </span>{' '}
      over the last {range} days
      {share != null && (
        <>
          {' — '}
          <span className="font-medium text-[var(--color-ink)]">{share.toFixed(1)}%</span> of the
          site's real-user traffic
        </>
      )}
      {rows.length > 1 && (
        <>
          , across{' '}
          {rows
            .map((r) => DEVICE_LABELS[r.form_factor] ?? r.form_factor)
            .join(', ')
            .toLowerCase()}
        </>
      )}
      .
    </p>
  );
}

/** The five metrics for the selected device, each against the same
 *  metric for the whole site on the same device. Same-device comparison
 *  matters: "phones in this segment vs the whole site including
 *  desktops" would make every mobile segment look broken. */
function VitalsTiles({
  row,
  baseRow,
  siteUuid,
  range,
  filters,
  device,
}: {
  row: PixelPerfRow;
  baseRow: PixelPerfRow | null;
  siteUuid: string;
  range: PageRange;
  filters: ProfileFilters;
  device: string;
}): ReactElement {
  const blockers = trendBlockers(filters);
  const trendable = blockers.length === 0;
  // Daily series for the spark bars under each tile. Skipped entirely
  // when the pipe can't honour every filter — an unfiltered trend drawn
  // under a segment's tiles is worse than no trend.
  const trend = useSitePixelPerfTrend(siteUuid, {
    days: range,
    ...profileFiltersToPerfOpts(filters),
    enabled: trendable,
  });

  const series = (trend.data ?? []).filter((r) => r.form_factor === device);

  const comparable = isComparable(filters);

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-[var(--color-ink)]">
          Vitals — {DEVICE_LABELS[device] ?? device}
        </h3>
        {!trendable && (
          <p className="text-xs text-[var(--color-mute)]">
            Day-by-day history can't be filtered by {blockers.join(', ')} yet, so the tiles show the
            period total only.
          </p>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        {METRICS.map((m) => {
          const value = m.p75(row);
          const base = baseRow ? m.p75(baseRow) : null;
          const bars = series.map((s) => ({
            x: new Date(s.day).getTime(),
            y: m.p75(s),
          }));
          return (
            <MetricTile
              key={m.key}
              // `band`, not the Overview's `chip`: five tiles across
              // ~190px each can't fit a label, a status chip AND a
              // delta pill, and the label was the thing that
              // truncated. The delta is what this page is for, so the
              // chip goes and the band rides on the value instead.
              variant="band"
              // A line, not bars: the window runs to 12 months, and at
              // 180+ daily points the bars are sub-pixel and read as
              // texture rather than a trend.
              chart="line"
              label={m.label}
              value={m.format(value)}
              band={m.band(value)}
              bars={bars}
              {...(comparable
                ? {
                    deltaPct: deltaPct(value, base),
                    compare: {
                      swatchColor: 'var(--color-mute)',
                      label: 'Whole site',
                      value: m.format(base),
                    },
                  }
                : {})}
            />
          );
        })}
      </div>
      <p className="text-xs text-[var(--color-mute)]">
        {comparable ? (
          <>
            Each tile compares this segment against every{' '}
            {(DEVICE_LABELS[device] ?? device).toLowerCase()} visitor to the site over the same
            window.{' '}
          </>
        ) : (
          <>
            This segment is every {(DEVICE_LABELS[device] ?? device).toLowerCase()} visitor to the
            site, so there's nothing narrower to compare it against.{' '}
          </>
        )}
        p75 — the experience the slowest quarter of visits beat.
      </p>
    </section>
  );
}

/** Every device the segment spans, so the selector's choice is never
 *  hiding a worse one. Only rendered for multi-device segments. */
function DeviceTable({
  rows,
  baseRows,
}: {
  rows: PixelPerfRow[];
  baseRows: PixelPerfRow[];
}): ReactElement {
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold text-[var(--color-ink)]">By device</h3>
      <MetricTable
        rows={rows.map((r) => ({
          key: r.form_factor,
          label: DEVICE_LABELS[r.form_factor] ?? r.form_factor,
          row: r,
          baseRow: baseRows.find((b) => b.form_factor === r.form_factor) ?? null,
        }))}
      />
    </section>
  );
}

/**
 * The segment split by its own filter values — one row per value, for
 * every dimension that names more than one.
 *
 * This is the "why" of the page. A segment called "EU mobile" spanning
 * five countries reports one p75; this table says which of the five is
 * dragging it. Each row is a separate query: the segment's filters with
 * that one dimension narrowed to a single value, so every row is a real
 * p75 of a real slice rather than a decomposition of the total.
 *
 * Bounded on purpose. A dimension listing twenty countries would mean
 * twenty raw-event queries on page load, so it caps at
 * `MAX_VALUES_PER_DIMENSION` and says so rather than quietly showing
 * the first few as if they were all of them.
 */
const MAX_VALUES_PER_DIMENSION = 6;

function ValueSplit({
  segment,
  siteUuid,
  range,
  device,
  segmentRow,
}: {
  segment: TrafficProfile;
  siteUuid: string;
  range: PageRange;
  device: string | null;
  segmentRow: PixelPerfRow | null;
}): ReactElement | null {
  const splittable = Object.entries(segment.filters).filter(
    ([, values]) => (values?.length ?? 0) > 1,
  );
  if (splittable.length === 0 || !device) return null;

  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-[var(--color-ink)]">Inside this segment</h3>
        <p className="mt-0.5 text-xs text-[var(--color-mute)]">
          Each value this segment accepts, measured on its own — on{' '}
          {(DEVICE_LABELS[device] ?? device).toLowerCase()}, against the segment's own numbers.
        </p>
      </div>
      {splittable.map(([dimension, values]) => (
        <DimensionSplit
          key={dimension}
          dimension={dimension}
          values={(values ?? []).slice(0, MAX_VALUES_PER_DIMENSION)}
          hidden={Math.max(0, (values?.length ?? 0) - MAX_VALUES_PER_DIMENSION)}
          filters={segment.filters}
          siteUuid={siteUuid}
          range={range}
          device={device}
          segmentRow={segmentRow}
        />
      ))}
    </section>
  );
}

function DimensionSplit({
  dimension,
  values,
  hidden,
  filters,
  siteUuid,
  range,
  device,
  segmentRow,
}: {
  dimension: string;
  values: Array<string | number>;
  hidden: number;
  filters: ProfileFilters;
  siteUuid: string;
  range: PageRange;
  device: string;
  segmentRow: PixelPerfRow | null;
}): ReactElement {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-[0.06em] text-[var(--color-mute)]">
          By {dimensionLabel(dimension).toLowerCase()}
        </h4>
        {hidden > 0 && (
          <span className="text-[11px] text-[var(--color-mute)]">
            showing {values.length} of {values.length + hidden}
          </span>
        )}
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--color-line-soft)] text-left">
            <th className="py-1.5 pr-3 font-medium text-[11px] uppercase tracking-[0.06em] text-[var(--color-mute)]">
              {dimensionLabel(dimension)}
            </th>
            {METRICS.map((m) => (
              <th
                key={m.key}
                className="py-1.5 pr-3 text-right font-medium text-[11px] uppercase tracking-[0.06em] text-[var(--color-mute)]"
              >
                {m.label}
              </th>
            ))}
            <th className="py-1.5 text-right font-medium text-[11px] uppercase tracking-[0.06em] text-[var(--color-mute)]">
              Samples
            </th>
          </tr>
        </thead>
        <tbody>
          {values.map((value) => (
            <ValueRow
              key={String(value)}
              dimension={dimension}
              value={value}
              filters={filters}
              siteUuid={siteUuid}
              range={range}
              device={device}
            />
          ))}
          {segmentRow && (
            <tr className="border-t border-[var(--color-line)]">
              <td className="py-1.5 pr-3 text-xs text-[var(--color-mute)]">All of the above</td>
              {METRICS.map((m) => (
                <td
                  key={m.key}
                  className={cn(
                    'py-1.5 pr-3 text-right tabular-nums',
                    bandText(m.band(m.p75(segmentRow))),
                  )}
                >
                  {m.format(m.p75(segmentRow))}
                </td>
              ))}
              <td className="py-1.5 text-right text-xs tabular-nums text-[var(--color-mute)]">
                {formatCount(segmentRow.lcp_sample_count)}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/** One value's slice: the segment's filters with `dimension` pinned to
 *  this single value, narrowed to the selected device so the row is
 *  comparable with the tiles above. */
function ValueRow({
  dimension,
  value,
  filters,
  siteUuid,
  range,
  device,
}: {
  dimension: string;
  value: string | number;
  filters: ProfileFilters;
  siteUuid: string;
  range: PageRange;
  device: string;
}): ReactElement {
  const opts = useMemo(
    () =>
      profileFiltersToPerfOpts({
        ...filters,
        [dimension]: [value],
      } as ProfileFilters),
    [filters, dimension, value],
  );
  const perf = useSitePixelPerf(siteUuid, { days: range, formFactors: [device], ...opts });
  const row = withSamples(perf.data).find((r) => r.form_factor === device) ?? null;

  return (
    <tr className="border-b border-[var(--color-line-soft)] last:border-b-0">
      <td className="py-1.5 pr-3 text-[var(--color-ink-soft)]">
        {describeValue(dimension, value)}
      </td>
      {METRICS.map((m) => (
        <td
          key={m.key}
          className={cn(
            'py-1.5 pr-3 text-right tabular-nums',
            row ? bandText(m.band(m.p75(row))) : 'text-[var(--color-mute)]',
          )}
        >
          {perf.isLoading ? '…' : row ? m.format(m.p75(row)) : '—'}
        </td>
      ))}
      <td className="py-1.5 text-right text-xs tabular-nums text-[var(--color-mute)]">
        {perf.isLoading ? '…' : row ? formatCount(row.lcp_sample_count) : '—'}
      </td>
    </tr>
  );
}

/** Shared metric table body — label column, one column per metric, a
 *  samples column. Used by the device breakdown. */
function MetricTable({
  rows,
}: {
  rows: Array<{
    key: string;
    label: string;
    row: PixelPerfRow;
    baseRow: PixelPerfRow | null;
  }>;
}): ReactElement {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-[var(--color-line-soft)] text-left">
          <th className="py-1.5 pr-3 font-medium text-[11px] uppercase tracking-[0.06em] text-[var(--color-mute)]">
            Device
          </th>
          {METRICS.map((m) => (
            <th
              key={m.key}
              className="py-1.5 pr-3 text-right font-medium text-[11px] uppercase tracking-[0.06em] text-[var(--color-mute)]"
            >
              {m.label}
            </th>
          ))}
          <th className="py-1.5 text-right font-medium text-[11px] uppercase tracking-[0.06em] text-[var(--color-mute)]">
            Samples
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className="border-b border-[var(--color-line-soft)] last:border-b-0">
            <td className="py-1.5 pr-3 text-[var(--color-ink-soft)]">{r.label}</td>
            {METRICS.map((m) => (
              <td
                key={m.key}
                className={cn(
                  'py-1.5 pr-3 text-right tabular-nums',
                  bandText(m.band(m.p75(r.row))),
                )}
              >
                {m.format(m.p75(r.row))}
                {r.baseRow && (
                  <span className="ml-1.5 text-[11px] text-[var(--color-mute)]">
                    vs {m.format(m.p75(r.baseRow))}
                  </span>
                )}
              </td>
            ))}
            <td className="py-1.5 text-right text-xs tabular-nums text-[var(--color-mute)]">
              {formatCount(r.row.lcp_sample_count)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
